import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, GeoPoint } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import ngeohash from 'ngeohash';
import AdminLayout from './AdminLayout';
import './AdminAccount.css';

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Parse subscription description: ***Title***^feature1^feature2
const parseDescription = (description) => {
  if (!description || !description.startsWith('***') || !description.includes('^')) {
    return { title: description || '', features: [] };
  }
  const parts = description.split('^');
  const title = parts[0].replace(/\*\*\*/g, '');
  const features = parts.slice(1);
  return { title, features };
};

const SubscriptionList = ({ subscriptions, isChangePlan, activeSubscriptionName, onSelect }) => {
  const filteredSubs = subscriptions.filter(sub => !isChangePlan || sub.name !== activeSubscriptionName);
  
  if (filteredSubs.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
        <p className="text-muted">
          {isChangePlan 
            ? 'You are already subscribed to the only available plan.' 
            : 'No subscription plans available.'}
        </p>
      </div>
    );
  }
  
  return (
    <div className="row">
      {filteredSubs.map((sub, index) => {
        const prices = sub.prices || [];
        const priceInfo = prices.length > 0 ? prices[0] : null;
        const priceAmount = priceInfo?.unitAmount || 0;
        const priceInterval = priceInfo?.interval || '';
        const priceText = priceAmount === 0 
          ? 'Free' 
          : `$${(priceAmount / 100).toFixed(2)}/${priceInterval}`;
        const { title, features } = parseDescription(sub.description);
        
        return (
          <div key={index} className="col-md-6 mb-4">
            <div className="card h-100 shadow-sm" style={{ borderRadius: '16px', border: 'none' }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h5 className="card-title font-weight-bold" style={{ color: '#ff6b35' }}>{sub.name}</h5>
                  <span 
                    className="badge badge-pill" 
                    style={{ 
                      backgroundColor: 'rgba(255,107,53,0.1)', 
                      color: '#ff6b35',
                      fontSize: '0.9rem',
                      padding: '6px 12px'
                    }}
                  >
                    {priceText}
                  </span>
                </div>
                
                {title && (
                  <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>{title}</p>
                )}
                
                {features.length > 0 && (
                  <div className="mb-4">
                    {features.map((feature, idx) => (
                      <div key={idx} className="d-flex align-items-center mb-2">
                        <i className="fas fa-check-circle text-success mr-2" style={{ fontSize: '0.8rem' }}></i>
                        <span style={{ fontSize: '0.85rem' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <button 
                  className="btn btn-primary btn-block"
                  style={{ 
                    backgroundColor: '#ff6b35', 
                    borderColor: '#ff6b35',
                    borderRadius: '12px',
                    padding: '10px'
                  }}
                  onClick={() => onSelect(sub)}
                >
                  {isChangePlan ? 'Change Plan' : 'Subscribe'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminAccountPage = () => {
  const formatCurrency = (v) => `$${(Number(v) || 0).toFixed(2)}`;
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState('user');

  // Payouts
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsView, setPayoutsView] = useState('future'); // 'future' | 'completed'
  const [payoutsTab, setPayoutsTab] = useState('summary'); // 'summary' | 'details' (Summary = the Stripe-style balance view)
  const [payoutBalance, setPayoutBalance] = useState(null);
  const [payoutDestination, setPayoutDestination] = useState(null); // {bankName, last4}
  const [pendingTransactions, setPendingTransactions] = useState([]);
  // Payout schedule
  const [payoutSchedule, setPayoutSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    interval: 'manual',
    delayDays: 2,
    weeklyAnchor: 'friday',
    monthlyAnchor: 1,
  });
   
  // Stripe status
  const [stripeStatus, setStripeStatus] = useState({
    connected: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false,
    loading: false
  });
  
  // Subscriptions
  const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [isChangePlan, setIsChangePlan] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
        setLoading(false);
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const searchAddress = async (query) => {
    if (!query || query.length < 3 || !MAPBOX_ACCESS_TOKEN) return;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&limit=5`
      );
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Mapbox search error:', error);
    }
  };

  const selectAddress = (place) => {
    if (userData) {
      const [longitude, latitude] = place.center || [null, null];
      setUserData({
        ...userData,
        stripeBusinessAddress: place.place_name,
        stripeBusinessLatitud: latitude,
        stripeBusinessLongitud: longitude
      });
    }
    setShowAddressSuggestions(false);
  };

  const handleInputChange = (field, value) => {
    setUserData({
      ...userData,
      [field]: value
    });
  };

  const hasBillingAccount = userData?.stripeAccountId && userData.stripeAccountId.length > 0;
  const chargesEnabled = stripeStatus.connected ? stripeStatus.chargesEnabled : (userData?.stripeChargesEnabled === true);
  const payoutsEnabled = stripeStatus.connected ? stripeStatus.payoutsEnabled : (userData?.stripePayoutsEnabled === true);
  const detailsSubmitted = stripeStatus.connected ? stripeStatus.detailsSubmitted : (userData?.stripeDetailsSubmitted === true);
  const isFullyVerified = chargesEnabled && payoutsEnabled && detailsSubmitted;

  // Active subscription data
  const activeSubscription = {
    id: userData?.activeSubscriptionId,
    name: userData?.activeSubscriptionName,
    description: userData?.activeSubscriptionDescription,
    status: userData?.activeSubscriptionStatus,
    startDate: userData?.activeSubscriptionStartDate,
    endDate: userData?.activeSubscriptionEndDate,
    cancelAt: userData?.activeSubscriptionCancelAt,
  };
  const hasActiveSubscription = !!activeSubscription.id;
  const isCanceled = activeSubscription.status === 'canceled' || !!activeSubscription.cancelAt;

  // ── Subscription billing (cards + invoice history) ─────────────────────
  // Loaded from Stripe via getSubscriptionBillingDetails only when the
  // Billing tab is open and a subscription exists.
  const [billingDetails, setBillingDetails] = useState(null); // { defaultPaymentMethodId, paymentMethods[], invoices[] }
  const [billingDetailsLoading, setBillingDetailsLoading] = useState(false);
  const [cardActionLoading, setCardActionLoading] = useState(false);

  const saveUserData = async () => {
    if (!user) return false;

    try {
      const db = getFirestore();
      const lat = userData?.stripeBusinessLatitud;
      const lng = userData?.stripeBusinessLongitud;
      const position = (lat != null && lng != null)
        ? { geohash: ngeohash.encode(lat, lng, 9), geopoint: new GeoPoint(lat, lng) }
        : null;
      await setDoc(doc(db, 'users', user.uid), {
        name: userData?.name || '',
        phone: userData?.phone || '',
        stripeBusinessLegalName: userData?.stripeBusinessLegalName || '',
        stripeDbaName: userData?.stripeDbaName || '',
        stripeBusinessType: userData?.stripeBusinessType || 'individual',
        stripeBusinessEmail: userData?.stripeBusinessEmail || user.email || '',
        stripeBusinessPhone: userData?.stripeBusinessPhone || '',
        stripeBusinessAddress: userData?.stripeBusinessAddress || '',
        stripeBusinessLatitud: lat ?? null,
        stripeBusinessLongitud: lng ?? null,
        position: position,
        stripeWebsite: userData?.stripeWebsite || '',
        stripeTaxId: userData?.stripeTaxId || '',
        stripeProductDescription: userData?.stripeProductDescription || '',
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving user data:', error);
      return false;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const success = await saveUserData();
    setLoading(false);
    
    if (success) {
      Swal.fire('Success', 'Changes saved successfully!', 'success');
    } else {
      Swal.fire('Error', 'Failed to save changes', 'error');
    }
  };

  const handleCreateBillingAccount = async () => {
    if (!user) return;
    
    if (!userData?.name || userData.name.trim().length === 0) {
      Swal.fire('Error', 'Business name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      
      const saved = await saveUserData();
      if (!saved) {
        throw new Error('Failed to save user data');
      }

      const functions = getFunctions();
      const createStripeAccount = httpsCallable(functions, 'createStripeAccount');
      
      const result = await createStripeAccount({
        email: user.email,
        businessName: userData?.stripeBusinessLegalName || userData?.name || '',
        userId: user.uid,
        dbaName: userData?.stripeDbaName || '',
        businessType: userData?.stripeBusinessType || 'individual',
        phone: userData?.phone || '',
        address: userData?.stripeBusinessAddress || '',
        website: userData?.stripeWebsite || '',
        taxId: userData?.stripeTaxId || '',
        productDescription: userData?.stripeProductDescription || '',
        baseUrl: window.location.origin,
      });

      const data = result.data;
      
      if (data.success) {
        await loadUserData();
        
        if (data.url) {
          window.open(data.url, '_blank');
        } else {
          Swal.fire('Success', 'Stripe account created successfully!', 'success');
        }
      } else {
        throw new Error(data.error || 'Failed to create Stripe account');
      }
    } catch (error) {
      console.error('Error creating billing account:', error);
      Swal.fire('Error', error.message || 'Failed to create billing account', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayouts = async () => {
    if (!user || !userData?.stripeAccountId) return;
    try {
      setPayoutsLoading(true);
      const functions = getFunctions();
      const getStripePayoutsFn = httpsCallable(functions, 'getStripePayouts');
      const result = await getStripePayoutsFn({});
      const data = result.data;
      if (data.success) {
        setPayouts(data.payouts || []);
        setPayoutBalance(data.balance || null);
        setPayoutDestination(data.payoutDestination || null);
        setPendingTransactions(data.pendingTransactions || []);
      }
    } catch (error) {
      console.error('Error fetching payouts:', error);
      Swal.fire('Error', error.message || 'Failed to load payouts', 'error');
    } finally {
      setPayoutsLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    if (!user || !userData?.stripeAccountId) return;
    
    try {
      setStripeStatus(prev => ({ ...prev, loading: true }));
      const functions = getFunctions();
      const getStripeAccountStatus = httpsCallable(functions, 'getStripeAccountStatus');
      
      const result = await getStripeAccountStatus({
        userId: user.uid,
      });

      const data = result.data || {};

      // Always clear the spinner — a response without `success` (older
      // function bundles, or the no-account early return) must not leave
      // "Loading payment settings…" up forever.
      setStripeStatus({
        connected: data.connected || false,
        chargesEnabled: data.chargesEnabled || false,
        payoutsEnabled: data.payoutsEnabled || false,
        detailsSubmitted: data.detailsSubmitted || false,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
      setStripeStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchAvailableSubscriptions = async () => {
    if (!user) return;
    
    try {
      setSubscriptionsLoading(true);
      const functions = getFunctions();
      const getSubscriptionProducts = httpsCallable(functions, 'getSubscriptionProducts');
      
      const result = await getSubscriptionProducts();
      const data = result.data;
      
      if (data.success && data.products) {
        // Filter products with platform:fodyfee metadata and sort by position
        const filtered = data.products
          .filter(p => p.metadata && p.metadata.platform === 'fodyfee')
          .sort((a, b) => {
            const posA = parseInt(a.metadata?.position || '999', 10);
            const posB = parseInt(b.metadata?.position || '999', 10);
            return posA - posB;
          });
        
        setAvailableSubscriptions(filtered);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      Swal.fire('Error', 'Failed to load subscription plans', 'error');
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const createSubscriptionCheckout = async (subscription) => {
    if (!user) return;
    
    const prices = subscription.prices || [];
    const priceId = prices.length > 0 ? prices[0].id : null;
    
    if (!priceId) {
      Swal.fire('Error', 'No price found for this subscription', 'error');
      return;
    }

    try {
      setLoading(true);
      const functions = getFunctions();
      const createSubscriptionCheckoutFn = httpsCallable(functions, 'createSubscriptionCheckout');
      
      const result = await createSubscriptionCheckoutFn({
        userId: user.uid,
        priceId: priceId,
        productName: subscription.name,
      });

      const data = result.data;
      
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.error || 'Failed to create checkout');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      Swal.fire('Error', error.message || 'Failed to create checkout', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscription = async () => {
    if (!user) return;
    
    // Check if billing account is created and fully verified
    if (!hasBillingAccount) {
      Swal.fire({
        title: 'Billing Account Required',
        text: 'Please create a billing account first to subscribe.',
        icon: 'warning',
        confirmButtonColor: '#ff6b35',
      });
      return;
    }
    
    if (!isFullyVerified) {
      Swal.fire({
        title: 'Account Not Verified',
        text: 'Please complete the Stripe onboarding process before subscribing.',
        icon: 'warning',
        confirmButtonColor: '#ff6b35',
        confirmButtonText: 'Complete Setup',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          handleOpenStripeDashboard();
        }
      });
      return;
    }
    
    // Fetch and show subscriptions
    setIsChangePlan(false);
    await fetchAvailableSubscriptions();
    setShowSubscriptionModal(true);
  };

  const handleOpenStripeDashboard = async () => {
    if (!user || !hasBillingAccount) return;
    
    try {
      setLoading(true);
      const functions = getFunctions();
      const createStripeAccountLink = httpsCallable(functions, 'createStripeAccountLink');
      
      const result = await createStripeAccountLink({
        userId: user.uid,
      });

      const data = result.data;
      
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.error || 'Failed to create account link');
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      Swal.fire('Error', error.message || 'Failed to open Stripe dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!user || !hasActiveSubscription) return;
    
    // Fetch and show subscriptions for changing plan
    setIsChangePlan(true);
    await fetchAvailableSubscriptions();
    setShowSubscriptionModal(true);
  };

  const changeSubscription = async (subscription) => {
    if (!user) return;
    
    const prices = subscription.prices || [];
    const priceId = prices.length > 0 ? prices[0].id : null;
    
    if (!priceId) {
      Swal.fire('Error', 'No price found for this subscription', 'error');
      return;
    }

    try {
      setLoading(true);
      const functions = getFunctions();
      const changeSubscriptionFn = httpsCallable(functions, 'changeSubscription');
      
      const result = await changeSubscriptionFn({
        userId: user.uid,
        newPriceId: priceId,
        newProductName: subscription.name,
      });

      const data = result.data;
      
      if (data.success) {
        await loadUserData();
        Swal.fire('Success', 'Subscription changed successfully!', 'success');
      } else {
        throw new Error(data.error || 'Failed to change subscription');
      }
    } catch (error) {
      console.error('Error changing subscription:', error);
      Swal.fire('Error', error.message || 'Failed to change subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || !hasActiveSubscription) return;
    
    const result = await Swal.fire({
      title: 'Cancel Subscription?',
      text: 'Are you sure you want to cancel? You will lose access to premium features at the end of your billing period.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Cancel at Period End',
      cancelButtonText: 'Keep Subscription',
      reverseButtons: true,
    });
    
    if (result.isConfirmed) {
      try {
        setLoading(true);
        const functions = getFunctions();
        const cancelSubscriptionFn = httpsCallable(functions, 'cancelSubscription');
        
        const cancelResult = await cancelSubscriptionFn({
          userId: user.uid,
        });

        const data = cancelResult.data;
        
        if (data.success) {
          await loadUserData();
          Swal.fire({
            title: 'Subscription Canceled',
            text: 'Your subscription will be canceled at the end of the billing period.',
            icon: 'info',
            confirmButtonColor: '#ff6b35',
          });
        } else {
          throw new Error(data.error || 'Failed to cancel subscription');
        }
      } catch (error) {
        console.error('Error canceling subscription:', error);
        Swal.fire('Error', error.message || 'Failed to cancel subscription', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRevertCancellation = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const functions = getFunctions();
      const revertCancellationFn = httpsCallable(functions, 'revertCancellation');
      
      const result = await revertCancellationFn({
        userId: user.uid,
      });

      const data = result.data;
      
      if (data.success) {
        await loadUserData();
        Swal.fire('Success', 'Subscription restored!', 'success');
      } else {
        throw new Error(data.error || 'Failed to restore subscription');
      }
    } catch (error) {
      console.error('Error reverting cancellation:', error);
      Swal.fire('Error', error.message || 'Failed to restore subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Subscription billing handlers (cards + invoices) ──────────────────
  const loadBillingDetails = async () => {
    if (!user || !hasActiveSubscription) return;
    setBillingDetailsLoading(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'getSubscriptionBillingDetails');
      const result = await fn({});
      const data = result.data;
      if (data.success) setBillingDetails(data);
    } catch (error) {
      console.error('Error loading billing details:', error);
    } finally {
      setBillingDetailsLoading(false);
    }
  };

  const handleRemoveCard = async (pm) => {
    if (!user) return;
    const isOnlyCard = (billingDetails?.paymentMethods?.length || 0) <= 1;
    if (isOnlyCard) {
      Swal.fire('Not allowed', 'You must keep at least one payment method on file for the subscription.', 'warning');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Remove this card?',
      html: `Card ending in <strong>•••• ${pm.last4}</strong> will be removed from your subscription billing.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Remove',
      cancelButtonText: 'Keep',
    });
    if (!confirm.isConfirmed) return;
    setCardActionLoading(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'detachSubscriptionPaymentMethod');
      const result = await fn({ paymentMethodId: pm.id });
      if (result.data.success) {
        await loadBillingDetails();
        Swal.fire({ title: 'Card removed', icon: 'success', timer: 1400, showConfirmButton: false });
      } else {
        throw new Error(result.data.error || 'Failed to remove card');
      }
    } catch (error) {
      const msg = error?.message || 'Failed to remove card';
      Swal.fire('Error', msg.includes('at least one payment method') ? 'You must keep at least one payment method on file.' : msg, 'error');
    } finally {
      setCardActionLoading(false);
    }
  };

  // Add/change the subscription card via a Stripe Checkout SETUP session;
  // the webhook makes the new card the subscription's default on return.
  const handleChangeCard = async () => {
    if (!user) return;
    setCardActionLoading(true);
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'createSubscriptionCardUpdateSession');
      const result = await fn({});
      const data = result.data;
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to start card update');
      }
    } catch (error) {
      console.error('Error starting card update:', error);
      Swal.fire('Error', error.message || 'Failed to start card update', 'error');
      setCardActionLoading(false);
    }
  };

  const fetchPayoutSchedule = async () => {
    if (!user || !userData?.stripeAccountId) return;
    try {
      setScheduleLoading(true);
      const functions = getFunctions();
      const getStripePayoutScheduleFn = httpsCallable(functions, 'getStripePayoutSchedule');
      const result = await getStripePayoutScheduleFn({});
      const data = result.data;
      if (data.success && data.schedule) {
        setPayoutSchedule(data.schedule);
        setScheduleForm({
          interval: data.schedule.interval || 'manual',
          delayDays: data.schedule.delayDays || 2,
          weeklyAnchor: data.schedule.weeklyAnchor || 'friday',
          monthlyAnchor: data.schedule.monthlyAnchor || 1,
        });
      }
    } catch (error) {
      console.error('Error fetching payout schedule:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  const updatePayoutSchedule = async () => {
    if (!user || !userData?.stripeAccountId) return;
    try {
      setScheduleLoading(true);
      const functions = getFunctions();
      const updateStripePayoutScheduleFn = httpsCallable(functions, 'updateStripePayoutSchedule');
      const payload = {
        interval: scheduleForm.interval,
        delayDays: parseInt(scheduleForm.delayDays, 10),
      };
      if (scheduleForm.interval === 'weekly') {
        payload.weeklyAnchor = scheduleForm.weeklyAnchor;
      }
      if (scheduleForm.interval === 'monthly') {
        payload.monthlyAnchor = parseInt(scheduleForm.monthlyAnchor, 10);
      }
      const result = await updateStripePayoutScheduleFn(payload);
      const data = result.data;
      if (data.success && data.schedule) {
        setPayoutSchedule(data.schedule);
        Swal.fire('Success', 'Payout schedule updated successfully!', 'success');
      }
    } catch (error) {
      console.error('Error updating payout schedule:', error);
      Swal.fire('Error', error.message || 'Failed to update payout schedule', 'error');
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadUserData = async () => {
    if (!user) return;
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error reloading user data:', error);
    }
  };

  // Fetch fresh Stripe status when billing tab is active and account exists
  useEffect(() => {
    if (activeTab === 'billing' && hasBillingAccount) {
      fetchStripeStatus();
    }
  }, [activeTab, hasBillingAccount, user]);

  // Fetch payouts and schedule when payouts tab is active
  useEffect(() => {
    if (activeTab === 'payouts' && hasBillingAccount) {
      fetchPayouts();
      fetchPayoutSchedule();
    }
  }, [activeTab, hasBillingAccount, user]);

  // Fetch subscription billing details (cards + invoice history) when the
  // billing tab is open and there's an active subscription
  useEffect(() => {
    if (activeTab === 'billing' && hasActiveSubscription) {
      loadBillingDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasActiveSubscription, user]);

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <AdminLayout title="Account">
      <div className="admin-account-page">
        {/* Tabs */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header p-0">
                <ul className="nav nav-tabs account-tabs">
                  <li className="nav-item">
                    <a 
                      className={`nav-link ${activeTab === 'user' ? 'active' : ''}`}
                      href="#"
                      onClick={(e) => { e.preventDefault(); setActiveTab('user'); }}
                    >
                      <i className="fas fa-user mr-2"></i>
                      User Information
                    </a>
                  </li>
                  <li className="nav-item">
                    <a 
                      className={`nav-link ${activeTab === 'billing' ? 'active' : ''}`}
                      href="#"
                      onClick={(e) => { e.preventDefault(); setActiveTab('billing'); }}
                    >
                      <i className="fas fa-credit-card mr-2"></i>
                      Billing Account
                    </a>
                  </li>
                  <li className="nav-item">
                    <a 
                      className={`nav-link ${activeTab === 'payouts' ? 'active' : ''}`}
                      href="#"
                      onClick={(e) => { e.preventDefault(); setActiveTab('payouts'); }}
                    >
                      <i className="fas fa-money-check-alt mr-2"></i>
                      Payouts Information
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* User Information Tab */}
        {activeTab === 'user' && (
          <>
            <div className="row">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header bg-orange">
                    <h3 className="card-title">
                      <i className="fas fa-user-circle mr-2"></i>
                      Profile Information
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label>Full Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-user"></i></span>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          value={userData?.name || ''}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Enter full name"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Phone Number</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-phone"></i></span>
                        </div>
                        <input
                          type="tel"
                          className="form-control"
                          value={userData?.phone || ''}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header bg-orange">
                    <h3 className="card-title">
                      <i className="fas fa-building mr-2"></i>
                      Business Details
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label>Business Legal Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-building"></i></span>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          value={userData?.stripeBusinessLegalName || ''}
                          onChange={(e) => handleInputChange('stripeBusinessLegalName', e.target.value)}
                          placeholder="Enter business legal name"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Doing Business As (DBA)</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-tag"></i></span>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          value={userData?.stripeDbaName || ''}
                          onChange={(e) => handleInputChange('stripeDbaName', e.target.value)}
                          placeholder="Enter DBA name"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-header bg-orange">
                <h3 className="card-title"><i className="fas fa-building mr-2"></i>Business Details</h3>
              </div>
              <div className="card-body">
                <div className="row mt-4">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Business Type</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-briefcase"></i></span>
                        </div>
                        <select
                          className="form-control"
                          value={userData?.stripeBusinessType || ''}
                          onChange={(e) => handleInputChange('stripeBusinessType', e.target.value)}
                        >
                          <option value="">Select business type</option>
                          <option value="individual">Individual</option>
                          <option value="llc">LLC</option>
                          <option value="corporation">Corporation</option>
                          <option value="partnership">Partnership</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Business Email</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-envelope"></i></span>
                        </div>
                        <input
                          type="email"
                          className="form-control"
                          value={userData?.stripeBusinessEmail || ''}
                          onChange={(e) => handleInputChange('stripeBusinessEmail', e.target.value)}
                          placeholder="Enter business email"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Business Phone</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-phone"></i></span>
                        </div>
                        <input
                          type="tel"
                          className="form-control"
                          value={userData?.stripeBusinessPhone || ''}
                          onChange={(e) => handleInputChange('stripeBusinessPhone', e.target.value)}
                          placeholder="Enter business phone"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Business Address</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-map-marker-alt"></i></span>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          value={userData?.stripeBusinessAddress || ''}
                          onChange={(e) => {
                            handleInputChange('stripeBusinessAddress', e.target.value);
                            searchAddress(e.target.value);
                          }}
                          placeholder="Search address..."
                        />
                      </div>
                      {showAddressSuggestions && addressSuggestions.length > 0 && (
                        <div className="list-group mt-1" style={{ position: 'absolute', zIndex: 1000, width: '100%' }}>
                          {addressSuggestions.map((place, index) => (
                            <button
                              key={index}
                              className="list-group-item list-group-item-action"
                              onClick={() => selectAddress(place)}
                            >
                              {place.place_name}
                            </button>
                          ))}
                        </div>
                      )}
                      {!MAPBOX_ACCESS_TOKEN && (
                        <small className="text-muted">Add REACT_APP_MAPBOX_TOKEN to your .env file to enable address search</small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Website URL</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-globe"></i></span>
                        </div>
                        <input
                          type="url"
                          className="form-control"
                          value={userData?.stripeWebsite || ''}
                          onChange={(e) => handleInputChange('stripeWebsite', e.target.value)}
                          placeholder="Enter website URL"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Tax ID (EIN)</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-id-card"></i></span>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          value={userData?.stripeTaxId || ''}
                          onChange={(e) => handleInputChange('stripeTaxId', e.target.value)}
                          placeholder="Enter Tax ID (EIN)"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Product Description</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text"><i className="fas fa-align-left"></i></span>
                        </div>
                        <textarea
                          className="form-control"
                          rows="1"
                          value={userData?.stripeProductDescription || ''}
                          onChange={(e) => handleInputChange('stripeProductDescription', e.target.value)}
                          placeholder="Enter product description"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <button 
                  className="btn btn-primary btn-lg"
                  onClick={handleSave}
                  disabled={loading}
                >
                  <i className="fas fa-save mr-2"></i>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button><br></br><br></br>
              </div>
            </div>
          </>
        )}

        {/* Billing Account Tab */}
        {activeTab === 'billing' && (
          <div className="row">
            {/* Payment Settings */}
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-header bg-orange">
                  <h3 className="card-title"><i className="fas fa-cog mr-2"></i>Payment Settings</h3>
                </div>
                <div className="card-body">
                  {stripeStatus.loading ? (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center h-100 py-5">
                      <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                      <p className="text-muted">Loading payment settings...</p>
                    </div>
                  ) : hasBillingAccount ? (
                    <>
                      <div className="d-flex align-items-center mb-3">
                        <i className="fas fa-check-circle text-success fa-2x mr-3"></i>
                        <div>
                          <h5 className="mb-0 font-weight-bold text-success">Billing Account Created</h5>
                          <small className="text-muted">Your account is ready to receive payments</small>
                        </div>
                      </div>

                      <hr />

                      {isFullyVerified ? (
                        <div className="alert alert-success d-flex align-items-center">
                          <i className="fas fa-check-circle mr-2"></i>
                          <span>Your account is ready to receive payments!</span>
                        </div>
                      ) : (
                        <div className="alert alert-warning">
                          <div className="d-flex align-items-start">
                            <i className="fas fa-info-circle mr-2 mt-1"></i>
                            <div>
                              <span>Your account was created. Complete your information in Stripe to start receiving payments.</span>
                              <br />
                              <small>Click "Open Stripe Dashboard" below to add required information.</small>
                            </div>
                          </div>
                        </div>
                      )}

                      <h6 className="font-weight-bold mb-3">Account Status</h6>
                      
                      <div className="mb-2 d-flex justify-content-between align-items-center">
                        <span><i className={`fas ${chargesEnabled ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} mr-2`}></i> Charges Enabled</span>
                        <span className={chargesEnabled ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>{chargesEnabled ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="mb-2 d-flex justify-content-between align-items-center">
                        <span><i className={`fas ${payoutsEnabled ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} mr-2`}></i> Payouts Enabled</span>
                        <span className={payoutsEnabled ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>{payoutsEnabled ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="mb-3 d-flex justify-content-between align-items-center">
                        <span><i className={`fas ${detailsSubmitted ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} mr-2`}></i> Details Submitted</span>
                        <span className={detailsSubmitted ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'}>{detailsSubmitted ? 'Yes' : 'No'}</span>
                      </div>

                      <button 
                        className="btn btn-outline-primary btn-block"
                        onClick={handleOpenStripeDashboard}
                      >
                        <i className="fas fa-external-link-alt mr-2"></i>
                        {isFullyVerified ? 'Open Stripe Dashboard' : 'Complete Stripe Setup'}
                      </button>
                    </>
                  ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center h-100">
                      <div className="mb-4">
                        <i className="fas fa-credit-card fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">No Billing Account</h5>
                        <p className="text-muted">Create a billing account to start accepting payments and manage subscriptions.</p>
                      </div>
                      <button 
                        className="btn btn-primary btn-lg"
                        onClick={handleCreateBillingAccount}
                      >
                        <i className="fas fa-plus mr-2"></i>Create Billing Account
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Active Subscription */}
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-header bg-orange">
                  <h3 className="card-title"><i className="fas fa-sync-alt mr-2"></i>Active Subscription</h3>
                </div>
                <div className="card-body">
                  {stripeStatus.loading ? (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center h-100 py-5">
                      <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                      <p className="text-muted">Loading subscription data...</p>
                    </div>
                  ) : hasActiveSubscription ? (
                    <div className={`p-4 rounded" style={{ background: isCanceled ? '#fff8e1' : '#e8f5e9', borderRadius: '16px' }}`}>
                      <div className="d-flex align-items-center mb-3">
                        <div 
                          className="d-flex align-items-center justify-content-center mr-3" 
                          style={{ 
                            width: 48, 
                            height: 48, 
                            borderRadius: 12, 
                            background: isCanceled ? 'rgba(255,152,0,0.2)' : 'rgba(76,175,80,0.2)',
                            color: isCanceled ? '#ff9800' : '#4caf50'
                          }}
                        >
                          <i className={`fas ${isCanceled ? 'fa-clock' : 'fa-check-circle'} fa-lg`}></i>
                        </div>
                        <div>
                          <h5 className="mb-0 font-weight-bold" style={{ color: isCanceled ? '#e65100' : '#2e7d32' }}>
                            {activeSubscription.name || 'Active Subscription'}
                          </h5>
                          <small style={{ color: isCanceled ? '#ef6c00' : '#388e3c' }}>
                            {isCanceled 
                              ? `Canceled - Ends ${formatDate(activeSubscription.endDate)}`
                              : `Renews ${formatDate(activeSubscription.endDate)}`
                            }
                          </small>
                        </div>
                      </div>
                      
                      {/* Subscription Features */}
                      {activeSubscription.description && (
                        <>
                          {(() => {
                            const { title, features } = parseDescription(activeSubscription.description);
                            return (
                              <>
                                {title && (
                                  <h6 className="font-weight-bold mt-3 mb-2" style={{ color: isCanceled ? '#e65100' : '#2e7d32', fontSize: '0.85rem' }}>
                                    {title}
                                  </h6>
                                )}
                                {features.length > 0 && (
                                  <div className="mb-3">
                                    {features.map((feature, idx) => (
                                      <div key={idx} className="d-flex align-items-center py-1" style={{ borderTop: idx > 0 ? '1px solid #e0e0e0' : 'none' }}>
                                        <i className="fas fa-check text-success mr-2" style={{ fontSize: '0.8rem' }}></i>
                                        <span style={{ fontSize: '0.9rem', color: '#616161' }}>{feature}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}

                      {isCanceled && (
                        <div className="alert alert-warning mt-3 mb-3 py-2 px-3" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-info-circle mr-2"></i>
                          Your subscription ended on {formatDate(activeSubscription.endDate)}. You can still use the benefits until then.
                        </div>
                      )}

                      {!isCanceled ? (
                        <div className="row mt-3">
                          <div className="col-6">
                            <button 
                              className="btn btn-outline-warning btn-block btn-sm"
                              onClick={handleChangePlan}
                            >
                              <i className="fas fa-exchange-alt mr-1"></i> Change Plan
                            </button>
                          </div>
                          <div className="col-6">
                            <button 
                              className="btn btn-outline-danger btn-block btn-sm"
                              onClick={handleCancelSubscription}
                            >
                              <i className="fas fa-times mr-1"></i> Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3">
                          <button 
                            className="btn btn-outline-primary btn-block btn-sm"
                            onClick={handleRevertCancellation}
                          >
                            <i className="fas fa-undo mr-1"></i> Undo Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center h-100">
                      <div className="mb-4">
                        <i className="fas fa-calendar-check fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">No Active Subscription</h5>
                        <p className="text-muted">Add a subscription plan to enable premium features.</p>
                      </div>
                      <button 
                        className="btn btn-primary btn-lg"
                        onClick={handleAddSubscription}
                        disabled={!hasBillingAccount}
                      >
                        <i className="fas fa-plus mr-2"></i>Add Subscription
                      </button>
                      {!hasBillingAccount && (
                        <small className="text-muted mt-2 d-block">
                          <i className="fas fa-info-circle mr-1"></i>Create a billing account to enable this option
                        </small>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

          {/* ── Subscription payment methods + invoice history ──────────
              Only with an active subscription. Cards and invoices come from
              Stripe (the merchant's platform customer) via
              getSubscriptionBillingDetails. Own conditional block (no
              fragment) so it can't unbalance the billing tab's JSX. */}
          {activeTab === 'billing' && hasActiveSubscription && (
            <div className="row mt-2">
              {/* Payment Methods */}
              <div className="col-lg-6 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-orange d-flex justify-content-between align-items-center">
                    <h3 className="card-title m-0"><i className="fas fa-credit-card mr-2"></i>Payment Method</h3>
                    <button
                      className="btn btn-light btn-sm"
                      onClick={handleChangeCard}
                      disabled={cardActionLoading}
                    >
                      {cardActionLoading
                        ? <i className="fas fa-spinner fa-spin"></i>
                        : <><i className="fas fa-plus mr-1"></i>Add / Change Card</>}
                    </button>
                  </div>
                  <div className="card-body">
                    {billingDetailsLoading ? (
                      <div className="text-center py-4">
                        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 60, height: 60, objectFit: 'contain' }} />
                        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>Loading cards…</p>
                      </div>
                    ) : (billingDetails?.paymentMethods?.length || 0) === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="fas fa-credit-card fa-2x mb-2 d-block"></i>
                        <small>No card on file. Add one to keep the subscription active.</small>
                      </div>
                    ) : (
                      <>
                        {billingDetails.paymentMethods.map((pm) => {
                          const brandIcon = { visa: 'fa-cc-visa', mastercard: 'fa-cc-mastercard', amex: 'fa-cc-amex', discover: 'fa-cc-discover' }[pm.brand] || 'fa-credit-card';
                          const isOnlyCard = billingDetails.paymentMethods.length <= 1;
                          return (
                            <div key={pm.id} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <div className="d-flex align-items-center">
                                <i className={`fab ${brandIcon} fa-2x mr-3`} style={{ color: '#555' }}></i>
                                <div>
                                  <div className="font-weight-bold" style={{ fontSize: '0.95rem' }}>
                                    {pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)} •••• {pm.last4}
                                    {pm.isDefault && (
                                      <span className="badge badge-success ml-2" style={{ fontSize: '0.65rem' }}>Default</span>
                                    )}
                                  </div>
                                  <small className="text-muted">Expires {String(pm.expMonth).padStart(2, '0')}/{pm.expYear}</small>
                                </div>
                              </div>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveCard(pm)}
                                disabled={cardActionLoading || isOnlyCard}
                                title={isOnlyCard ? 'You must keep at least one payment method' : 'Remove this card'}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          );
                        })}
                        <small className="text-muted d-block mt-2">
                          <i className="fas fa-info-circle mr-1"></i>The default card is charged for your Fodyfee subscription. A subscription must always keep at least one card.
                        </small>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Billing History */}
              <div className="col-lg-6 mb-4">
                <div className="card h-100">
                  <div className="card-header bg-orange">
                    <h3 className="card-title m-0"><i className="fas fa-file-invoice-dollar mr-2"></i>Billing History</h3>
                  </div>
                  <div className="card-body">
                    {billingDetailsLoading ? (
                      <div className="text-center py-4">
                        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 60, height: 60, objectFit: 'contain' }} />
                        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>Loading invoices…</p>
                      </div>
                    ) : (billingDetails?.invoices?.length || 0) === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="fas fa-file-invoice fa-2x mb-2 d-block"></i>
                        <small>No charges yet. Your subscription invoices will appear here.</small>
                      </div>
                    ) : (
                      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {billingDetails.invoices.map((inv) => {
                          const statusColor = { paid: 'badge-success', open: 'badge-warning', void: 'badge-secondary', uncollectible: 'badge-danger' }[inv.status] || 'badge-light';
                          const downloadUrl = inv.hostedInvoiceUrl || inv.invoicePdf;
                          return (
                            <div key={inv.id} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <div>
                                <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                                  {inv.number}
                                  <span className={`badge ${statusColor} ml-2`} style={{ fontSize: '0.6rem' }}>{(inv.status || '').toUpperCase()}</span>
                                </div>
                                <small className="text-muted">{inv.created ? formatDate(inv.created) : ''}</small>
                              </div>
                              <div className="d-flex align-items-center">
                                <span className="font-weight-bold mr-3" style={{ color: '#ff6b35' }}>
                                  ${inv.amountPaid.toFixed(2)} {inv.currency}
                                </span>
                                {downloadUrl && (
                                  <a
                                    href={downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-primary btn-sm"
                                    title="Download invoice"
                                  >
                                    <i className="fas fa-download"></i>
                                  </a>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
             </div>
           )}
 
         {/* Payouts Information Tab */}
        {activeTab === 'payouts' && (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header bg-orange d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <h3 className="card-title m-0 mr-3"><i className="fas fa-money-check-alt mr-2"></i>Payouts Information</h3>
                    <div className="btn-group btn-group-sm">
                      <button
                        className={`btn ${payoutsTab === 'summary' ? 'btn-light' : 'btn-outline-light'}`}
                        onClick={() => setPayoutsTab('summary')}
                        style={payoutsTab === 'summary' ? { backgroundColor: '#fff', color: '#ff6b35', fontWeight: 700 } : { color: '#fff' }}
                      >Summary</button>
                      <button
                        className={`btn ${payoutsTab === 'details' ? 'btn-light' : 'btn-outline-light'}`}
                        onClick={() => setPayoutsTab('details')}
                        style={payoutsTab === 'details' ? { backgroundColor: '#fff', color: '#ff6b35', fontWeight: 700 } : { color: '#fff' }}
                      >Details</button>
                    </div>
                  </div>
                  {!payoutsLoading && payouts.length > 0 && (
                    <div className="btn-group btn-group-sm">
                      <button
                        className={`btn ${payoutsView === 'future' ? 'btn-light' : 'btn-outline-light'}`}
                        onClick={() => setPayoutsView('future')}
                        style={payoutsView === 'future' ? { backgroundColor: '#fff', color: '#ff6b35', fontWeight: 700 } : { color: '#fff' }}
                      >
                        <i className="fas fa-clock mr-1"></i>Future
                      </button>
                      <button
                        className={`btn ${payoutsView === 'completed' ? 'btn-light' : 'btn-outline-light'}`}
                        onClick={() => setPayoutsView('completed')}
                        style={payoutsView === 'completed' ? { backgroundColor: '#fff', color: '#ff6b35', fontWeight: 700 } : { color: '#fff' }}
                      >
                        <i className="fas fa-check-circle mr-1"></i>Completed
                      </button>
                    </div>
                  )}
                </div>
                <div className="card-body">
                  {payoutsLoading ? (
                    <div className="text-center py-5">
                      <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                      <p className="mt-2 text-muted">Loading payouts...</p>
                    </div>
                  ) : !hasBillingAccount ? (
                    <div className="text-center py-5">
                      <i className="fas fa-university fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No Billing Account</h5>
                      <p className="text-muted">Create a billing account to view payout information.</p>
                    </div>
                  ) : payoutsTab === 'summary' ? (
                    <div>
                      {/* Big balance (available now) */}
                      <h2 className="font-weight-bold" style={{ fontSize: 34 }}>
                        {payoutBalance?.available?.length
                          ? `$${payoutBalance.available.reduce((a, b) => a + b.amount, 0).toFixed(2)}`
                          : '$0.00'}
                      </h2>
                      <p className="text-muted" style={{ fontSize: 13 }}>Balance available to pay out</p>

                      {/* Balance summary */}
                      <div className="card" style={{ borderRadius: 12, border: '1px solid #e5e7eb' }}>
                        <div className="card-body p-0">
                          <div className="d-flex justify-content-between align-items-center px-3 py-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <span><i className="fas fa-circle mr-2" style={{ color: '#94a3b8', fontSize: 10 }}></i>Incoming</span>
                            <strong>{payoutBalance?.pending?.length
                              ? `$${payoutBalance.pending.reduce((a, b) => a + b.amount, 0).toFixed(2)}`
                              : '$0.00'}</strong>
                          </div>
                          <div className="d-flex justify-content-between align-items-center px-3 py-3">
                            <span><i className="fas fa-circle mr-2" style={{ color: '#8b5cf6', fontSize: 10 }}></i>Available</span>
                            <strong>{payoutBalance?.available?.length
                              ? `$${payoutBalance.available.reduce((a, b) => a + b.amount, 0).toFixed(2)}`
                              : '$0.00'}</strong>
                          </div>
                        </div>
                      </div>

                      {/* Recent activity */}
                      <h6 className="font-weight-bold mt-4 mb-2">Recent activity</h6>
                      {(() => {
                        const sorted = [...payouts].sort((a, b) => (b.arrivalDate || 0) - (a.arrivalDate || 0));
                        if (sorted.length === 0) return <p className="text-muted" style={{ fontSize: 13 }}>No payouts yet.</p>;
                        return (
                          <div className="table-responsive">
                            <table className="table table-sm" style={{ fontSize: 13 }}>
                              <thead>
                                <tr>
                                  <th>Amount</th>
                                  <th>Status</th>
                                  <th>Destination</th>
                                  <th>Type</th>
                                  <th className="text-right">Arrive by</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sorted.slice(0, 20).map((p) => {
                                  const upcoming = p.status === 'pending' || p.status === 'in_transit' || p.status === 'upcoming';
                                  return (
                                    <tr key={p.id}>
                                      <td className="font-weight-bold">{formatCurrency(p.amount)}</td>
                                      <td>
                                        <span className={`badge ${upcoming ? 'badge-info' : 'badge-success'}`}>
                                          {upcoming ? 'Upcoming' : 'Paid'}
                                        </span>
                                      </td>
                                      <td>{payoutDestination ? `${payoutDestination.bankName} •••• ${payoutDestination.last4}` : '—'}</td>
                                      <td className="text-muted">Payout to bank account</td>
                                      <td className="text-right text-muted">
                                        {p.arrivalDate ? new Date(p.arrivalDate * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <>
                      {/* Payout Schedule Editor */}
                      <div className="card mb-4" style={{ borderRadius: 12, border: '1px solid #e0e0e0' }}>
                        <div className="card-body p-3">
                          <h6 className="font-weight-bold mb-3" style={{ color: '#ff6b35' }}>
                            <i className="fas fa-cog mr-2"></i>Payout Schedule
                          </h6>
                          <div className="row align-items-end">
                            <div className="col-md-3">
                              <label style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>Mode</label>
                              <select
                                className="form-control"
                                value={scheduleForm.interval}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, interval: e.target.value })}
                                disabled={scheduleLoading}
                              >
                                <option value="manual">Manual</option>
                                <option value="daily">Automatic - Daily</option>
                                <option value="weekly">Automatic - Weekly</option>
                                <option value="monthly">Automatic - Monthly</option>
                              </select>
                            </div>
                            {scheduleForm.interval !== 'manual' && (
                              <>
                                {scheduleForm.interval === 'weekly' && (
                                  <div className="col-md-3">
                                    <label style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>Day of Week</label>
                                    <select
                                      className="form-control"
                                      value={scheduleForm.weeklyAnchor}
                                      onChange={(e) => setScheduleForm({ ...scheduleForm, weeklyAnchor: e.target.value })}
                                      disabled={scheduleLoading}
                                    >
                                      <option value="monday">Monday</option>
                                      <option value="tuesday">Tuesday</option>
                                      <option value="wednesday">Wednesday</option>
                                      <option value="thursday">Thursday</option>
                                      <option value="friday">Friday</option>
                                      <option value="saturday">Saturday</option>
                                      <option value="sunday">Sunday</option>
                                    </select>
                                  </div>
                                )}
                                {scheduleForm.interval === 'monthly' && (
                                  <div className="col-md-3">
                                    <label style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>Day of Month</label>
                                    <select
                                      className="form-control"
                                      value={scheduleForm.monthlyAnchor}
                                      onChange={(e) => setScheduleForm({ ...scheduleForm, monthlyAnchor: parseInt(e.target.value, 10) })}
                                      disabled={scheduleLoading}
                                    >
                                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                        <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                                <div className="col-md-3">
                                  <label style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>Delay (days)</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    min={2}
                                    max={30}
                                    value={scheduleForm.delayDays}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, delayDays: e.target.value })}
                                    disabled={scheduleLoading}
                                  />
                                </div>
                              </>
                            )}
                            <div className={`col-md-${scheduleForm.interval === 'manual' ? '9' : '3'}`}>
                              <button
                                className="btn btn-primary btn-block"
                                style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
                                onClick={updatePayoutSchedule}
                                disabled={scheduleLoading}
                              >
                                {scheduleLoading ? (
                                  <span className="spinner-border spinner-border-sm mr-2"></span>
                                ) : (
                                  <i className="fas fa-save mr-2"></i>
                                )}
                                Save Schedule
                              </button>
                            </div>
                          </div>
                          {payoutSchedule && (
                            <div className="mt-2">
                              <small className="text-muted">
                                <i className="fas fa-info-circle mr-1"></i>
                                Current: <strong>{payoutSchedule.interval === 'manual' ? 'Manual' : `Automatic (${payoutSchedule.interval})`}</strong>
                                {payoutSchedule.interval === 'weekly' && payoutSchedule.weeklyAnchor && ` on ${payoutSchedule.weeklyAnchor.charAt(0).toUpperCase() + payoutSchedule.weeklyAnchor.slice(1)}s`}
                                {payoutSchedule.interval === 'monthly' && payoutSchedule.monthlyAnchor && ` on the ${payoutSchedule.monthlyAnchor}${payoutSchedule.monthlyAnchor === 1 ? 'st' : payoutSchedule.monthlyAnchor === 2 ? 'nd' : payoutSchedule.monthlyAnchor === 3 ? 'rd' : 'th'}`}
                                {payoutSchedule.delayDays !== undefined && `, delayed ${payoutSchedule.delayDays} day${payoutSchedule.delayDays !== 1 ? 's' : ''}`}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Balance Summary */}
                      {payoutBalance && (
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <div className="card" style={{ borderRadius: 12, border: '1px solid #e0e0e0', background: '#f0f8f0' }}>
                              <div className="card-body p-3 d-flex align-items-center">
                                <div className="mr-3" style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(76,175,80,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="fas fa-check-circle text-success" style={{ fontSize: 20 }}></i>
                                </div>
                                <div>
                                  <div style={{ fontSize: 14, color: '#666' }}>Available</div>
                                  <div style={{ fontSize: 22, fontWeight: 800, color: '#4caf50' }}>
                                    {payoutBalance.available?.length > 0
                                      ? payoutBalance.available.map(b => `$${b.amount.toFixed(2)} ${b.currency}`).join(', ')
                                      : '$0.00'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="card" style={{ borderRadius: 12, border: '1px solid #e0e0e0', background: '#fff8e1' }}>
                              <div className="card-body p-3 d-flex align-items-center">
                                <div className="mr-3" style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,152,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="fas fa-clock text-warning" style={{ fontSize: 20 }}></i>
                                </div>
                                <div>
                                  <div style={{ fontSize: 14, color: '#666' }}>Incoming (Pending)</div>
                                  <div style={{ fontSize: 22, fontWeight: 800, color: '#ff9800' }}>
                                    {payoutBalance.pending?.length > 0
                                      ? payoutBalance.pending.map(b => `$${b.amount.toFixed(2)} ${b.currency}`).join(', ')
                                      : '$0.00'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(() => {
                        if (payoutsView === 'future') {
                          const filteredPayouts = payouts.filter(p => ['pending', 'in_transit'].includes(p.status));
                          return (
                            <>
                              {/* Pending Transactions (Incoming) */}
                              {pendingTransactions.length > 0 && (
                                <>
                                  <h6 className="font-weight-bold mb-3" style={{ color: '#ff9800' }}>
                                    <i className="fas fa-arrow-down mr-2"></i>Incoming Transactions
                                  </h6>
                                  <div className="table-responsive mb-4">
                                    <table className="table table-hover table-sm">
                                      <thead className="thead-light">
                                        <tr>
                                          <th>ID</th>
                                          <th>Amount</th>
                                          <th>Description</th>
                                          <th>Date</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {pendingTransactions.map(t => (
                                          <tr key={t.id}>
                                            <td><code style={{ fontSize: '0.8rem' }}>{t.id}</code></td>
                                            <td className="font-weight-bold" style={{ color: '#ff9800' }}>
                                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: t.currency }).format(t.amount)}
                                            </td>
                                            <td style={{ fontSize: '0.85rem' }}>{t.description || '—'}</td>
                                            <td style={{ fontSize: '0.85rem' }}>{t.created ? new Date(t.created * 1000).toLocaleDateString() : 'N/A'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}

                              {/* Upcoming Payouts */}
                              {filteredPayouts.length > 0 && (
                                <>
                                  <h6 className="font-weight-bold mb-3" style={{ color: '#007bff' }}>
                                    <i className="fas fa-calendar-alt mr-2"></i>Scheduled Payouts
                                  </h6>
                                  <div className="table-responsive">
                                    <table className="table table-hover table-sm">
                                      <thead className="thead-light">
                                        <tr>
                                          <th>ID</th>
                                          <th>Amount</th>
                                          <th>Status</th>
                                          <th>Arrival Date</th>
                                          <th>Method</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {filteredPayouts.map(p => (
                                          <tr key={p.id}>
                                            <td><code style={{ fontSize: '0.8rem' }}>{p.id}</code></td>
                                            <td className="font-weight-bold" style={{ color: '#ff6b35' }}>
                                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: p.currency }).format(p.amount)}
                                            </td>
                                            <td>
                                              <span className={`badge badge-${
                                                p.status === 'pending' ? 'warning' : 'info'
                                              }`}>
                                                {p.status === 'in_transit' ? 'In Transit' : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                                              </span>
                                            </td>
                                            <td style={{ fontSize: '0.85rem' }}>{p.arrivalDate ? new Date(p.arrivalDate * 1000).toLocaleDateString() : 'N/A'}</td>
                                            <td style={{ fontSize: '0.85rem' }}>{p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1) : 'Standard'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}

                              {pendingTransactions.length === 0 && filteredPayouts.length === 0 && (
                                <div className="text-center py-5">
                                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                  <p className="text-muted">No upcoming payouts or incoming transactions.</p>
                                </div>
                              )}
                            </>
                          );
                        }

                        // Completed view
                        const filtered = payouts.filter(p => ['paid', 'canceled', 'failed'].includes(p.status));
                        if (filtered.length === 0) {
                          return (
                            <div className="text-center py-5">
                              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                              <p className="text-muted">No completed payouts yet.</p>
                            </div>
                          );
                        }
                        return (
                          <div className="table-responsive">
                            <table className="table table-hover table-sm">
                              <thead className="thead-light">
                                <tr>
                                  <th>ID</th>
                                  <th>Amount</th>
                                  <th>Status</th>
                                  <th>Arrival Date</th>
                                  <th>Method</th>
                                  <th>Destination</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filtered.map(p => (
                                  <tr key={p.id}>
                                    <td><code style={{ fontSize: '0.8rem' }}>{p.id}</code></td>
                                    <td className="font-weight-bold" style={{ color: '#ff6b35' }}>
                                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: p.currency }).format(p.amount)}
                                    </td>
                                    <td>
                                      <span className={`badge badge-${
                                        p.status === 'paid' ? 'success' :
                                        p.status === 'canceled' ? 'secondary' :
                                        'danger'
                                      }`}>
                                        {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: '0.85rem' }}>{p.arrivalDate ? new Date(p.arrivalDate * 1000).toLocaleDateString() : 'N/A'}</td>
                                    <td style={{ fontSize: '0.85rem' }}>{p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1) : 'Standard'}</td>
                                    <td><code style={{ fontSize: '0.8rem' }}>{p.destination || '—'}</code></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Selection Modal */}
        {showSubscriptionModal && (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title font-weight-bold">
                    {isChangePlan ? 'Change Subscription Plan' : 'Choose a Subscription Plan'}
                  </h5>
                  <button type="button" className="close" onClick={() => { setShowSubscriptionModal(false); setIsChangePlan(false); }}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {subscriptionsLoading ? (
                    <div className="text-center py-5">
                      <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
                      <p className="mt-2 text-muted">Loading subscription plans...</p>
                    </div>
                  ) : availableSubscriptions.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No subscription plans available.</p>
                    </div>
                  ) : (
                    <SubscriptionList 
                      subscriptions={availableSubscriptions}
                      isChangePlan={isChangePlan}
                      activeSubscriptionName={activeSubscription.name}
                      onSelect={(sub) => {
                        setShowSubscriptionModal(false);
                        if (isChangePlan) {
                          changeSubscription(sub);
                        } else {
                          createSubscriptionCheckout(sub);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAccountPage;